import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { SwPush } from '@angular/service-worker';
import {
  GetConfigRequest,
  RegisterSubscriptionRequest,
  RemoveSubscriptionRequest,
} from '@bp-core/src/lib/proto/portal-web-push.pb';
import { PortalWebPushGrpcServiceClient } from '@bp-core/src/lib/proto/portal-web-push.pbsc';
import { AuthService } from '@bp-core/src/lib/services/portal/auth.service';
import { StringValue } from '@ngx-grpc/well-known-types';
import { isNativeApp } from 'bp/shared/utils/native-app.utils';
import { EMPTY, catchError, firstValueFrom, take } from 'rxjs';

/**
 * Estado del canal de avisos del navegador para esta combinación de navegador y cliente.
 *
 * - `unsupported`: el navegador no tiene service worker (o no está registrado en este entorno).
 * - `unavailable`: el servidor tiene el canal apagado o sin clave VAPID.
 * - `impersonating`: la sesión es de un asesor o administrador que está "viendo como" cliente; el
 *   canal no corresponde (su navegador no debe quedar suscrito a los avisos del cliente).
 * - `blocked`: el navegador tiene las notificaciones denegadas para el sitio.
 * - `inactive`: se puede activar (falta el permiso o la suscripción).
 * - `active`: hay suscripción registrada en el servidor.
 * - `native`: el portal corre dentro de la app de Betterplan, que ya entrega sus propios avisos.
 */
export type PortalPushStatus =
  | 'unsupported'
  | 'unavailable'
  | 'impersonating'
  | 'blocked'
  | 'inactive'
  | 'active'
  | 'native';

/**
 * Desenlace de un intento de activación disparado por el usuario.
 *
 * `blocked` (el usuario rechazó el permiso) NO es un error: la pantalla lo cuenta con el estado,
 * no con un toast. Solo `failed` amerita avisar que algo salió mal.
 */
export type PortalPushActivationResult = 'active' | 'blocked' | 'failed' | 'unavailable';

/** El servidor recorta el user agent a 300; se manda ya recortado para no depender de eso. */
const USER_AGENT_MAX_LENGTH = 300;

/** Destino cuando el aviso no trae ruta (o trae una vacía). */
const DEFAULT_NOTIFICATION_URL = '/dashboard';

/**
 * Avisos del navegador (Web Push con VAPID) para el cliente del portal.
 *
 * Reglas del contrato que este servicio hace cumplir:
 * - El permiso del navegador se pide SOLO desde un gesto del usuario (`activate()`), nunca al cargar.
 * - En cada carga se re-registra la suscripción existente si ya hay permiso (`syncOnLoad()`), porque
 *   `pushsubscriptionchange` no es confiable entre navegadores y el endpoint puede rotar. El
 *   registro es idempotente por endpoint.
 * - Dentro de la app de Betterplan NO se suscribe nada: los avisos ya llegan por el push nativo y
 *   duplicarlos sería mostrar dos veces lo mismo.
 * - En una sesión "viendo como" cliente (asesor o administrador) tampoco se registra ni se consulta
 *   nada: el navegador del asesor no debe quedar suscrito a los avisos de su cliente.
 * - Nunca se registran ni se loguean el endpoint ni las claves de la suscripción.
 */
@Injectable({ providedIn: 'root' })
export class PortalPushService {
  /**
   * `SwPush` lo provee `ServiceWorkerModule`; se inyecta opcional para que el servicio no explote
   * donde ese módulo no está registrado (specs aislados, entornos sin service worker).
   */
  private readonly swPush = inject(SwPush, { optional: true });
  private readonly client = inject(PortalWebPushGrpcServiceClient);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  readonly status = signal<PortalPushStatus>('inactive');

  /** Último endpoint registrado en el servidor, para poder darlo de baja. */
  private currentEndpoint: string | null = null;
  private syncStarted = false;

  /**
   * Activa el canal. SOLO debe llamarse desde un clic: acá es donde el navegador pide permiso.
   */
  async activate(): Promise<PortalPushActivationResult> {
    const blockedReason = this.unavailableReason();
    if (blockedReason) {
      this.status.set(blockedReason);
      return 'unavailable';
    }

    if (!this.swPush?.isEnabled) {
      this.status.set('unsupported');
      return 'unavailable';
    }

    const config = await this.loadConfig();
    if (!config.enabled || !config.publicKey) {
      this.status.set('unavailable');
      return 'unavailable';
    }

    let subscription: PushSubscription;
    try {
      subscription = await this.swPush.requestSubscription({ serverPublicKey: config.publicKey });
    } catch {
      // El usuario rechazó el permiso o el navegador falló: se refleja el estado, no se reintenta.
      await this.refreshStatus();
      return this.status() === 'blocked' ? 'blocked' : 'failed';
    }

    if (!(await this.register(subscription))) {
      await this.refreshStatus();
      return 'failed';
    }

    this.status.set('active');
    return 'active';
  }

  /** Da de baja el destino en el navegador y en el servidor. */
  async deactivate(): Promise<void> {
    const endpoint = this.currentEndpoint;

    try {
      await this.swPush?.unsubscribe();
    } catch {
      // Si el navegador ya no tenía suscripción, igual hay que limpiar el servidor.
    }

    if (endpoint) {
      await firstValueFrom(
        this.client.removeSubscription(new RemoveSubscriptionRequest({ endpoint })).pipe(
          take(1),
          catchError(() => EMPTY),
          takeUntilDestroyed(this.destroyRef),
        ),
        { defaultValue: undefined },
      );
    }

    this.currentEndpoint = null;
    this.status.set('inactive');
  }

  /**
   * Re-registra la suscripción existente al cargar la app y deja escuchando el clic sobre un aviso.
   * NUNCA pide permiso: si el cliente todavía no lo dio, esto no hace nada y el botón del perfil
   * sigue siendo el único camino para activarlo.
   */
  syncOnLoad(): void {
    if (this.syncStarted) {
      return;
    }
    this.syncStarted = true;

    const blockedReason = this.unavailableReason();
    if (blockedReason) {
      this.status.set(blockedReason);
      return;
    }

    void this.refreshStatus();

    if (!this.swPush?.isEnabled) {
      return;
    }

    // El service worker solo enfoca la pestaña (`focusLastFocusedOrOpen`); navegar es cosa de la
    // app, así se llega al destino sin recargar la SPA ni perder el flujo a medio camino.
    this.swPush.notificationClicks
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ notification }) => {
        const data = notification?.data as { url?: string } | undefined;
        void this.router.navigateByUrl(data?.url || DEFAULT_NOTIFICATION_URL);
      });

    if (this.permission() !== 'granted') {
      return;
    }

    this.swPush.subscription.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(subscription => {
      if (!subscription) {
        return;
      }

      void this.register(subscription).then(registered => {
        if (registered) {
          this.status.set('active');
        }
      });
    });
  }

  /** Recalcula el estado con lo que dicen el navegador y el servidor. */
  async refreshStatus(): Promise<void> {
    const blockedReason = this.unavailableReason();
    if (blockedReason) {
      this.status.set(blockedReason);
      return;
    }

    if (!this.swPush?.isEnabled) {
      this.status.set('unsupported');
      return;
    }

    const config = await this.loadConfig();
    if (!config.enabled || !config.publicKey) {
      this.status.set('unavailable');
      return;
    }

    const permission = this.permission();
    if (permission === 'denied') {
      this.status.set('blocked');
      return;
    }

    this.status.set(permission === 'granted' && this.currentEndpoint ? 'active' : 'inactive');
  }

  /**
   * Estados en los que el canal no corresponde y NO se llama al servidor. Se resuelve antes que
   * cualquier otra cosa para que ni la app nativa ni una sesión impersonada generen tráfico.
   *
   * Ambos casos son distintos de `unavailable` (el servidor apagó el canal): el cliente merece
   * saber POR QUÉ no puede activarlas, sobre todo el asesor que mira la pantalla de su cliente.
   */
  private unavailableReason(): PortalPushStatus | null {
    if (isNativeApp()) {
      return 'native';
    }

    // Un manager operando como su empresa NO es impersonación elevada: registra el navegador bajo
    // la empresa y recibe sus avisos, igual que hoy en la app.
    if (this.authService.bpEnterAsId && this.authService.getUserIsAdmin()) {
      return 'impersonating';
    }

    return null;
  }

  /** Devuelve `true` solo si el servidor aceptó el registro. */
  private async register(subscription: PushSubscription): Promise<boolean> {
    const payload = subscription.toJSON();
    const endpoint = payload.endpoint ?? subscription.endpoint;
    const p256dh = payload.keys?.['p256dh'] ?? '';
    const auth = payload.keys?.['auth'] ?? '';

    if (!endpoint || !p256dh || !auth) {
      return false;
    }

    const response = await firstValueFrom(
      this.client
        .registerSubscription(
          new RegisterSubscriptionRequest({
            endpoint,
            p256dh,
            auth,
            userAgent: new StringValue({
              value: navigator.userAgent.slice(0, USER_AGENT_MAX_LENGTH),
            }),
          }),
        )
        .pipe(
          take(1),
          catchError(() => EMPTY),
          takeUntilDestroyed(this.destroyRef),
        ),
      { defaultValue: undefined },
    );

    if (!response) {
      return false;
    }

    this.currentEndpoint = endpoint;
    return true;
  }

  private async loadConfig(): Promise<{ enabled: boolean; publicKey: string }> {
    const response = await firstValueFrom(
      this.client.getConfig(new GetConfigRequest()).pipe(
        take(1),
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      ),
      { defaultValue: undefined },
    );

    return {
      enabled: response?.enabled ?? false,
      publicKey: response?.vapidPublicKey ?? '',
    };
  }

  /** `Notification` no existe en todos los contextos (WebView vieja, SSR): se lee defensivamente. */
  private permission(): NotificationPermission {
    const api = (window as unknown as { Notification?: { permission: NotificationPermission } })
      .Notification;
    return api?.permission ?? 'default';
  }
}
